import { createSlice } from "@reduxjs/toolkit";
import APIService from "../services/api.service";
import { AppDispatch } from "../store";
import { reportsGetAll } from "./reportsSlice";

const initialState = {
  commentDialogOpen: false,
  commentDrawerOpen: false,
  comments: [],
  commentsLoading: false,
  commentIsLoading: false,
  commentStatus: null,
  commentNextAction: null,
  commentId: null,
  message: '',
  title: '',
  importance: 0,
  status: 0,
  url: '',
  isShared: false,
  isEditabled: true,
  isDuplicated: false,
};

export const comment = createSlice({
  name: "comment",
  initialState,
  reducers: {
    openCommentDialog: (state, action) => {
      state.commentDialogOpen = true;
      if (action.payload.isDuplicated === true) {
        state.commentId = null;
        state.message = `${action.payload.element.message} - copy`;
      } else {
        state.commentId = action.payload.element.id;
        state.message = action.payload.element.message;
        state.url = action.payload.element.url;
      }
      state.title = action.payload.element.title || '';
      state.importance = action.payload.element.importance || 0;
      state.status = action.payload.element.status || 0;
      state.isShared = action.payload.element.isShared;
      // Other
      state.isEditabled = action.payload.isEditabled;
      state.isDuplicated = action.payload.isDuplicated;
    },
    closeCommentDialog: (state) => {
      state.commentDialogOpen = false;
    },
    openCommentDrawer: (state) => {
      state.commentDrawerOpen = true;
    },
    closeCommentDrawer: (state) => {
      state.commentDrawerOpen = false;
    },
    setComments: (state, action) => {
      state.comments = action.payload ? action.payload : [];
      state.commentsLoading = false;
    },
    loadingComments: (state) => {
      state.commentsLoading = true;
    },
    loadingComment: (state) => {
      state.commentIsLoading = true;
    },
    resetComment: (state, action) => {
      state.commentIsLoading = false;
      state.commentStatus = 'success';
      state.commentNextAction = action.payload;
    },
    resetCommentStatus: (state) => {
      state.commentStatus = null;
    }
  },
});

export const {
  openCommentDialog,
  closeCommentDialog,
  openCommentDrawer,
  closeCommentDrawer,
  setComments,
  loadingComments,
  loadingComment,
  resetComment,
  resetCommentStatus
} = comment.actions;

export default comment.reducer;

// Loads the project's comments into the slice (the drawer reads from here, so it
// isn't coupled to the Reports slice).
export const commentsGetAll = () => async (dispatch: AppDispatch) => {
  try {
    dispatch(loadingComments());
    const { data } = await APIService.get("/comments?limit=1000");
    dispatch(setComments(data.items || []));
  } catch (e) {
    dispatch(setComments([]));
  }
};

export const commentPost = (nextAction, data: any) => async (dispatch: AppDispatch) => {
  try {
    dispatch(loadingComment());
    await APIService.post("/comments", data).then((response) => {
      dispatch(resetComment(nextAction));
    });
    // Refresh both the drawer list and the Reports table so neither diverges.
    dispatch(commentsGetAll());
    dispatch(reportsGetAll());
  } catch (e) {
    // dispatch(setCustomerError());
  }
};


// Save edits made in the dialog. PUT /comments/:id, whose body is
// PutCommentBody — note the wire name is is_shared here while POST expects
// isShared, and every field is written unconditionally, so status has to be
// passed back or an archived comment would silently become current again.
// Mentions are not part of that endpoint, so editing does not change them.
export const commentUpdate = (nextAction, id: string, data: any) => async (dispatch: AppDispatch) => {
  try {
    dispatch(loadingComment());
    await APIService.put(`/comments/${id}`, {
      message: data.message,
      title: data.title,
      status: data.status,
      importance: data.importance,
      url: data.url,
      is_shared: data.isShared,
    });
    dispatch(resetComment(nextAction));
    dispatch(commentsGetAll());
    dispatch(reportsGetAll());
  } catch (e) {
  }
};

// Update a comment's open/completed status without opening the dialog.
export const commentSetStatus = (item: any, status: number) => async (dispatch: AppDispatch) => {
  try {
    await APIService.put(`/comments/${item.id}`, {
      message: item.message,
      title: item.title,
      status,
      importance: item.importance,
      url: item.url,
      // Wire field is is_shared (PutCommentBody), but the API returns isShared —
      // reading the wrong one sent undefined, which Go turned into false and
      // silently made every archived comment private.
      is_shared: item.isShared,
    });
    dispatch(commentsGetAll());
    dispatch(reportsGetAll());
  } catch (e) {
  }
};


export const commentDelete = (id: string) => async (dispatch: AppDispatch) => {
  try {
    await APIService.delete(`/comments/${id}`).then((response) => {
    });
    dispatch(commentsGetAll());
    dispatch(reportsGetAll());
  } catch (e) {
  }
};

// Toggle the current user's emoji reaction on a comment, then refresh the list.
export const commentReactionToggle = (commentId: string, emoji: string) => async (dispatch: AppDispatch) => {
  try {
    await APIService.post(`/comments/${commentId}/reactions`, { emoji });
    dispatch(commentsGetAll());
  } catch (e) {
  }
};
